import NotificationPreview from "./NotificationPreview";
import { Schema } from "@avenueops/frontend-shared";
import { MetricMetaKeys } from "@avenueops/shared-types";
import { SmartForm, Pill, TipTapEditor } from "@avenueops/web-components";
import { MessageFormattingType, WorkflowTriggerType } from "@prisma/client";
import { useEnrichmentColumns, useQueryColumns, useQueryRunResults } from "context/QueryRunContext";
import React, { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { trpc } from "utils/trpc";
import { getDefaultTitleFormat } from "utils/workflows";

export function CustomizeSignalSection() {
  const { watch, control, setValue } = useFormContext();

  const emoji = watch("emoji");
  const title = watch("title");
  const titleFormat = watch("titleFormat");
  const lastTitle = useRef<string>(title);
  const lastEmoji = useRef<string>(emoji);
  const formTemplateId = watch("formTemplateId");
  const workflowTriggerType = watch("workflowTriggerType");
  const workflowType = watch("workflowType");
  const aggregateResults = watch("aggregateResults");
  const _workflowType = watch("_workflowType");

  const settings = [
    {
      value: MessageFormattingType.PER_ROW,
      label: "Per Row",
      description: "Format and display a message for each row returned",
    },

    {
      value: MessageFormattingType.PER_MESSAGE,
      label: "Once",
      description: "Only format and display the first row of aggregated changes",
    },
  ];
  const { data: selectedForm, isLoading: formLoading } = trpc.forms.getForm.useQuery(
    { formTemplateId },
    { enabled: formTemplateId !== null && _workflowType === "FORM" },
  );
  const templateData = !formLoading ? (selectedForm?.templateJson as unknown as Schema) : undefined;
  const templateDataFormatOptions = templateData?.elements
    ? Object.entries(templateData.elements).reduce((acc, [, element]) => {
        if ("dataId" in element && element.dataId !== undefined) {
          acc.push(element.dataId);
        }
        return acc;
      }, [] as string[])
    : [];
  const { queryRunResults } = useQueryRunResults();
  const queryColumns = useQueryColumns();
  const enrichmentColumns = useEnrichmentColumns();
  const formatOptionsOne = workflowType === "METRIC" ? MetricMetaKeys : [...new Set([...queryColumns, ...enrichmentColumns])];

  React.useEffect(() => {
    // Check if the current title format is default. If so, update title format as you update the title.
    if (getDefaultTitleFormat(lastEmoji.current, lastTitle.current) === titleFormat) {
      setValue("titleFormat", getDefaultTitleFormat(emoji, title));
    }
    // Update last title and emoji
    lastEmoji.current = emoji;
    lastTitle.current = title;
  }, [emoji, title, setValue, titleFormat]);

  const titleFormatConfig = { name: "titleFormat", includeTitle: true };
  const messageFormatConfig = { name: "messageFormat", useMarkdown: true };
  const formatOptions =
    workflowTriggerType !== WorkflowTriggerType.WEBHOOK && workflowTriggerType !== WorkflowTriggerType.INTEGRATION
      ? formatOptionsOne
      : templateDataFormatOptions;

  const markdownContainerRef = useRef<HTMLDivElement>(null);
  const titleText: string = watch(titleFormatConfig.name) ?? "";
  const messageText: string = watch(messageFormatConfig.name) ?? "";
  const aveTemplateReg = RegExp(/({{\h?\S+\h?}})/g);
  const multiWordVariable = RegExp(/((\w+\s+)+)/g);
  return (
    <div className="rounded-xl py-5">
      <div className="gap-y-4 pb-5">
        <div className="flex flex-1 items-end">
          {workflowTriggerType !== WorkflowTriggerType.WEBHOOK && workflowTriggerType !== WorkflowTriggerType.INTEGRATION ? (
            <div className="flex flex-col gap-y-4">
              <SmartForm.Toggle name="aggregateResults" title="Aggregate Changes" />
            </div>
          ) : null}
        </div>
        {aggregateResults ? (
          <div className="flex flex-col gap-y-2">
            <div className="font-medium text-gray-900 text-sm mt-4">How would you like to format the alert body?</div>
            <SmartForm.RadioList name="messageFormattingType" options={settings} />
          </div>
        ) : null}
      </div>
      <div className="flex flex-row justify-between gap-4">
        <div className="flex-1">
          {titleFormatConfig.includeTitle ? (
            <>
              <SmartForm.TextInput
                name={titleFormatConfig.name}
                title="Title"
                syntaxHighlight={() => {
                  const textVals = titleText.split(aveTemplateReg);
                  if (textVals.length === 1) {
                    return titleText;
                  }
                  const children = textVals.map((val, i) => {
                    if (i % 2 === 0) {
                      return val;
                    }
                    return (
                      <span key={`titleText_${i}`} className="text-green-800">
                        {val}
                      </span>
                    );
                  });

                  return <>{children}</>;
                }}
              />
            </>
          ) : null}
        </div>
      </div>

      <div className="pt-2">
        <Controller
          control={control}
          name={messageFormatConfig.name}
          render={({ field: { value: messageFormat, onChange: onMessageFormatChange } }) => (
            <div className="min-h-[250px] flex overflow-hidden">
              <TipTapEditor
                name={messageFormatConfig.name}
                showToolbar
                initialValue={messageFormat}
                onChange={onMessageFormatChange}
                templateableValues={formatOptions?.map((option) => {
                  if (multiWordVariable.test(option)) {
                    return `[${option}]`;
                  }
                  return option;
                })}
              />
            </div>
          )}
        />
      </div>
      <div className="flex flex-col gapy-y-4">
        {queryRunResults && queryRunResults.length > 0 ? (
          // Add some padding so users can overscroll and see the preview closer to the middle of the page
          <div className="pb-60">
            <NotificationPreview />
          </div>
        ) : workflowTriggerType === WorkflowTriggerType.RAW_SQL ? (
          <div className="mt-1 text-sm leading-5 text-gray-500 font-medium">
            💡 Please run the SQL query to see the data that can be embedded in the title and message as well as a preview of your signal.
          </div>
        ) : workflowTriggerType === WorkflowTriggerType.DATA_VIEW ? (
          <div className="mt-1 text-sm leading-5 text-gray-500 font-medium">
            💡 Please preview the selected Data View to see the data that can be embedded in the title and message as well as a preview of your signal.
          </div>
        ) : null}
      </div>
    </div>
  );
}
